import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ordersAPI } from '../services/api';
import { 
  FiShoppingBag, 
  FiClock, 
  FiCheckCircle, 
  FiCreditCard,
  FiTrendingUp,
  FiUserPlus,
  FiEdit3,
  FiTag
} from 'react-icons/fi';

const Dashboard = () => {
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    revenue: 0,
    pendingRevenue: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const response = await ordersAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const statCards = [
    {
      title: 'Total Orders',
      value: stats.total,
      icon: FiShoppingBag,
      color: 'bg-blue-500',
      link: '/orders',
    },
    {
      title: 'Pending Orders',
      value: stats.pending,
      icon: FiClock,
      color: 'bg-yellow-500',
      link: '/orders?status=pending',
    },
    {
      title: 'Completed Orders',
      value: stats.completed,
      icon: FiCheckCircle,
      color: 'bg-green-500',
      link: '/orders?status=completed',
    },
    {
      title: 'Total Revenue',
      value: `₨ ${stats.revenue?.toFixed(0) || '0.00'}`,
      icon: FiCreditCard,
      color: 'bg-purple-500',
      currency: 'PKR'
    },
    {
      title: 'Pending Revenue',
      value: `₨ ${stats.pendingRevenue?.toFixed(0) || '0.00'}`,
      icon: FiTrendingUp,
      color: 'bg-orange-500',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
        <p className="text-gray-600 mt-2">Overview of your tailor shop</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          const content = (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-2xl hover:-translate-y-1 transition transform">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 dark:text-gray-300 text-sm font-medium">{card.title}</p>
                  <p className="text-2xl font-bold text-gray-800 dark:text-white mt-2">{card.value}</p>
                </div>
                <div className={`${card.color} p-3 rounded-lg`}>
                  <Icon className="text-white" size={24} />
                </div>
              </div>
            </div>
          );

          return card.link ? (
            <Link key={index} to={card.link}>
              {content}
            </Link>
          ) : (
            <div key={index}>{content}</div>
          );
        })}
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <Link
              to="/customers/new"
              className="flex items-center justify-between w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white py-3 px-4 rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition"
            >
              <span className="flex items-center space-x-2 font-medium">
                <FiUserPlus />
                <span>Add New Customer</span>
              </span>
              <span className="text-sm text-white/80">Start</span>
            </Link>
            <Link
              to="/measurements/new"
              className="flex items-center justify-between w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white py-3 px-4 rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition"
            >
              <span className="flex items-center space-x-2 font-medium">
                <FiEdit3 />
                <span>Add New Measurement</span>
              </span>
              <span className="text-sm text-white/80">Record</span>
            </Link>
            <Link
              to="/orders/new"
              className="flex items-center justify-between w-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white py-3 px-4 rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition"
            >
              <span className="flex items-center space-x-2 font-medium">
                <FiTag />
                <span>Create New Order</span>
              </span>
              <span className="text-sm text-white/80">Sell</span>
            </Link>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3">Welcome</h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            Manage your tailor shop efficiently with this comprehensive system.
            Track customers, measurements, and orders all in one place. Switch to dark mode anytime.
          </p>
          <div className="mt-4 h-1 w-24 bg-gradient-to-r from-primary-500 to-indigo-500 rounded-full" />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

